import { useEffect, useMemo, useState } from "react";
import { formatDateTime, formatNumber, formatRelativeTime } from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import { projectUniversalCompletionOverview } from "@excellent/core/verification-universal-surfaces-client";
import type { VerificationReceiptDto } from "@excellent/ui/api/contracts/verification-prove";
import {
  classifyWorkSubject,
  type WorkChainSubjectClass,
} from "@excellent/ui/api/contracts/verification-work-chain";
import type { VerificationEvidenceArtifactDto } from "@excellent/ui/api/contracts/verification-evidence";
import { verificationRegistrar } from "@excellent/ui/api/contracts/verification-registrar";
import type {
  RegistrarAssurancePropertyDto,
  RegistrarHolderClassDto,
} from "@excellent/ui/api/contracts/verification-registrar";
import { href } from "../../nav";
import { useVerificationEnvOptional } from "../../env-provider";
import {
  canonicalEnvironmentAvailable,
  useCanonicalEnvironmentOptional,
} from "../../canonical-environment-provider";
import { resolveReceiptVerdicts, verdictFrom } from "../../resolved-verdicts";
import {
  ActionButton,
  AssuranceSupport,
  ButtonRow,
  Callout,
  Card,
  CardContent,
  DataTable,
  Field,
  Fields,
  FilterField,
  FilterToolbar,
  ListSearch,
  Mono,
  ScopeTabs,
  Section,
  Stat,
  StatGrid,
  ToneBadge,
  Verdict,
  type DataTableColumn,
} from "../../ui";
import {
  PageError,
  PageFrame,
  PageLink,
  PagePending,
  type VerificationDetailPageProps,
  type VerificationPageProps,
} from "../page-frame";
import { verificationCanonicalClients } from "../../api/canonical-clients";
import { decodedReceiptId, SignedReceiptDetailPage } from "./signed-detail";
import {
  combineCompletionHistory,
  currentAuthorityIds,
  currentProspectiveReceipts,
  retrospectiveCompletionHistory,
} from "../../completion-history";

type ReceiptRow = VerificationReceiptDto;
type ReceiptArtifactRow = {
  id: string;
  label: string;
  sourceKind: string;
  contentHash: string;
  state: VerificationEvidenceArtifactDto["state"];
};

const RECEIPTS_OPEN_DETAIL_ACTION = "receipts.open-detail";
const RECEIPTS_EXPORT_ACTION = "receipts.export-envelope";
const RECEIPTS_FILTER_ALL = "receipts.filter-all";
const RECEIPTS_FILTER_PASS = "receipts.filter-pass";
const RECEIPTS_FILTER_FAIL = "receipts.filter-fail";
const RECEIPTS_FILTER_SUPERSEDED = "receipts.filter-superseded";

const RECEIPTS_LEDE =
  "Signed verifier output: each proof binds a frozen contract, worker attempt, admitted evidence graph, determinations, replay state, and trust key.";

/**
 * D1 item 6 — the verdict has to read without decoding an identifier.
 *
 * One plain sentence for what the verifier recorded. It describes the receipt
 * and claims nothing beyond it: no independence, no completion, no enforcement
 * authority. The badge columns still carry the machine-readable verdict.
 */
function receiptOutcomeSentence(status: string): string {
  if (status === "PASS") return "The verifier accepted this attempt.";
  // Ahead of the tail, which says "reached no verdict" — the verifier reached a
  // very definite one.
  if (status === "MISMATCH") {
    return "An independent record contradicted this attempt's claim.";
  }
  if (status === "FAIL") return "The verifier rejected this attempt.";
  if (status === "NOT_RUN") return "The verifier did not run the checks.";
  return "The verifier reached no verdict.";
}

const UNNAMED_WORK = "Work item outside the current ledger";

const RECEIPTS_SUBJECT_FILTER_ALL = "receipts.subject-filter-all";
const RECEIPTS_SUBJECT_FILTER_REAL = "receipts.subject-filter-real-subject";
const RECEIPTS_SUBJECT_FILTER_SELF_TEST = "receipts.subject-filter-self-test-fixture";
const RECEIPTS_SUBJECT_FILTER_SYNTHETIC = "receipts.subject-filter-synthetic-fixture";

/** Short row/tab label for a Work subject class — `classifyWorkSubject`'s own
 * `label` field is a full sentence meant for a detail view; lists need a word. */
const SUBJECT_CLASS_SHORT_LABEL: Record<WorkChainSubjectClass, string> = {
  "real-subject": "Real subject",
  "self-test-fixture": "Self-test",
  "synthetic-fixture": "Synthetic",
};

function subjectTone(subjectClass: WorkChainSubjectClass): "accent" | "unknown" {
  return subjectClass === "real-subject" ? "accent" : "unknown";
}

function useLastFetchedAt(data: unknown): string | null {
  const [lastFetchedAt, setLastFetchedAt] = useState<string | null>(null);
  useEffect(() => {
    if (data) setLastFetchedAt(new Date().toISOString());
  }, [data]);
  return lastFetchedAt;
}

function LastFetched({ at, stale }: { at: string | null; stale?: boolean }) {
  if (!at) return null;
  return (
    <Callout
      tone={stale ? "unknown" : "neutral"}
      title={stale ? "Showing stale data" : "Receipt projection loaded"}
    >
      Last fetched {formatDateTime(at)}.
    </Callout>
  );
}

function artifactRows(
  receipt: ReceiptRow,
  artifacts: VerificationEvidenceArtifactDto[],
): ReceiptArtifactRow[] {
  return artifacts
    .filter((artifact) => receipt.evidenceHashes.includes(artifact.contentHash))
    .map((artifact) => ({
      id: artifact.id,
      label: artifact.label,
      sourceKind: artifact.sourceKind,
      contentHash: artifact.contentHash,
      state: artifact.state,
    }));
}

function childReceipt(receipts: ReceiptRow[], id: string): ReceiptRow | null {
  return receipts.find((row) => row.parentReceiptId === id) ?? null;
}

function triggerDownload(filename: string, body: string) {
  if (typeof document === "undefined") return;
  const blob = new Blob([body], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function exportReceiptRecord(receipt: ReceiptRow) {
  triggerDownload(
    `${receipt.id}-receipt-record.json`,
    JSON.stringify(
      {
        receiptId: receipt.id,
        runId: receipt.runId,
        status: receipt.status,
        issuedAt: receipt.issuedAt,
        engineVersion: receipt.engineVersion,
        payloadHash: receipt.payloadHash,
        reportedSignatureMetadata: {
          keyId: receipt.signatureKeyId,
          algorithm: receipt.signatureAlgorithm,
        },
        evidenceHashes: receipt.evidenceHashes,
        policyVersionRefs: receipt.policyVersionRefs,
        cryptographicVerification: {
          status: "unavailable",
          reason: "This projection does not include a signed envelope or trust material.",
        },
        supersession: {
          parentReceiptId: receipt.parentReceiptId,
          supersedes: receipt.supersedes ?? null,
          correctionReason: receipt.correctionReason ?? null,
        },
        provenance: receipt.provenance,
      },
      null,
      2,
    ),
  );
}

function receiptsColumns(
  rows: ReceiptRow[],
  onNavigate: (to: string) => void,
): DataTableColumn<ReceiptRow>[] {
  return [
    {
      key: "receipt",
      header: "Receipt",
      cellClassName: "whitespace-normal",
      render: (receipt) => {
        const correction = childReceipt(rows, receipt.id);
        return (
          <div
            className="flex flex-col gap-0.5"
            data-receipt-id={receipt.id}
            data-demo-scoped={receipt.provenance.demoScoped ? "true" : undefined}
          >
            <Mono>{receipt.id}</Mono>
            {receipt.supersedes ? (
              <span className="type-subtext text-muted-foreground">
                corrects {receipt.supersedes}
              </span>
            ) : null}
            {correction ? (
              <span className="type-subtext text-muted-foreground">
                superseded by {correction.id}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "run",
      header: "Run",
      render: (receipt) => <Mono>{receipt.runId}</Mono>,
    },
    {
      key: "verdict",
      header: "Verdict",
      render: (receipt) => <Verdict status={receipt.status} />,
    },
    {
      key: "binding",
      header: "Bound facts",
      render: (receipt) => (
        <span>{`${formatNumber(receipt.checkCount)} checks · ${formatNumber(receipt.evidenceHashes.length)} evidence · ${formatNumber(receipt.policyVersionRefs.length)} policies`}</span>
      ),
    },
    {
      key: "key",
      header: "Reported key",
      render: (receipt) => <Mono>{receipt.signatureKeyId}</Mono>,
    },
    {
      key: "integrity",
      header: "Integrity",
      render: () => <ToneBadge tone="unknown">unavailable</ToneBadge>,
    },
    {
      key: "issued",
      header: "Issued",
      render: (receipt) => <Mono>{formatRelativeTime(receipt.issuedAt)}</Mono>,
    },
    {
      key: "actions",
      header: "Actions",
      render: (receipt) => (
        <ButtonRow>
          <ActionButton
            actionId={RECEIPTS_OPEN_DETAIL_ACTION}
            data-action-id={RECEIPTS_OPEN_DETAIL_ACTION}
            variant="primary"
            data-receipt-id={receipt.id}
            onClick={(event) => {
              event.stopPropagation();
              onNavigate(href("receipts", receipt.id));
            }}
          >
            Open detail
          </ActionButton>
        </ButtonRow>
      ),
    },
  ];
}

const REGISTRAR_HOLDER_CLASS_LABEL: Record<RegistrarHolderClassDto, string> = {
  "internal-independent": "Internal independent",
  counterparty: "Counterparty",
  notary: "Notary",
  operated: "Operated",
};

const REGISTRAR_SELECT_HOLDER_CLASS_ACTION = "registrar.select-holder-class";

const REGISTRAR_SECTION_NOTE =
  "A registrar countersignature is a second, independently held signature over a content-free " +
  "descriptor of a verification — digests and identifiers only, never evidence or source. What it " +
  "is worth depends entirely on who holds the registrar's key. The matrix below is read from the " +
  "registrar module itself, not paraphrased, so it cannot drift from what a countersignature actually asserts.";

/**
 * Registrar countersignature — assurance matrix.
 *
 * `independent-of-acting-agent` is deliberately never `yes` here: it is a
 * property of a specific receipt's execution boundaries, not of the holder
 * class, and this reference view has no receipt to evaluate. Every holder
 * class therefore renders it `unknown`, with the full note explaining that
 * distinct actor ids are process-distinct, not party-distinct — the honest
 * state, not a blank.
 */
function RegistrarAssuranceSection() {
  const matrixApi = useApi(verificationRegistrar.assuranceMatrix, {});
  const profiles = matrixApi.data?.profiles ?? [];
  const [holderClass, setHolderClass] = useState<RegistrarHolderClassDto>(
    "internal-independent",
  );
  const selected =
    profiles.find((profile) => profile.holderClass === holderClass) ?? profiles[0] ?? null;

  // Property order + question text read from whichever profile loaded first —
  // identical across all four holder classes by construction, so this never
  // depends on which tab is selected.
  const propertyOrder: { property: RegistrarAssurancePropertyDto; question: string }[] =
    (profiles[0]?.rows ?? []).map((row) => ({ property: row.property, question: row.question }));

  return (
    <Section title="Registrar countersignature" note={REGISTRAR_SECTION_NOTE}>
      {matrixApi.error ? (
        <Callout tone="unknown" title="Assurance matrix unavailable">
          The registrar assurance matrix could not be loaded from the server.
        </Callout>
      ) : matrixApi.isLoading && profiles.length === 0 ? (
        <PagePending title="Loading assurance matrix">
          Reading the registrar assurance matrix from the registrar module.
        </PagePending>
      ) : (
        <>
          {matrixApi.data ? (
            <Callout tone="neutral" title="What this does not claim">
              {matrixApi.data.limits}
            </Callout>
          ) : null}
          <DataTable
            rows={propertyOrder}
            rowKey={(row) => row.property}
            emptyState={{
              title: "No assurance rows",
              description: "The registrar assurance matrix has not loaded.",
            }}
            columns={[
              {
                key: "property",
                header: "Assurance property",
                cellClassName: "whitespace-normal",
                render: (row) => row.question,
              },
              ...profiles.map(
                (profile): DataTableColumn<{ property: RegistrarAssurancePropertyDto; question: string }> => ({
                  key: profile.holderClass,
                  header: REGISTRAR_HOLDER_CLASS_LABEL[profile.holderClass],
                  render: (row) => {
                    const cell = profile.rows.find((r) => r.property === row.property);
                    return cell ? <AssuranceSupport support={cell.support} /> : "—";
                  },
                }),
              ),
            ]}
          />
          <ScopeTabs
            value={holderClass}
            onChange={setHolderClass}
            options={profiles.map((profile) => ({
              key: profile.holderClass,
              label: REGISTRAR_HOLDER_CLASS_LABEL[profile.holderClass],
              actionId: `${REGISTRAR_SELECT_HOLDER_CLASS_ACTION}.${profile.holderClass}`,
            }))}
          />
          {selected ? (
            <Card>
              <CardContent>
                <Fields>
                  {selected.rows.map((row) => (
                    <Field key={row.property} label={row.question}>
                      <div className="flex flex-col gap-1.5">
                        <AssuranceSupport support={row.support} />
                        <span className="type-body text-foreground">{row.note}</span>
                      </div>
                    </Field>
                  ))}
                </Fields>
              </CardContent>
            </Card>
          ) : null}
        </>
      )}
    </Section>
  );
}

export function ReceiptsPage({
  onNavigate,
  now = new Date().toISOString(),
}: VerificationPageProps & {
  /**
   * THE RENDER EDGE. One clock read for this whole screen. Every count, badge and
   * sentence below is derived from `resolvedVerdicts`, which is built from THIS
   * instant — so the tab counts can never disagree with the rows they count.
   */
  now?: string;
}) {
  const env = useVerificationEnvOptional();
  const canonical = useCanonicalEnvironmentOptional();
  const ready = env?.ready ?? true;
  const environment = env?.environment ?? null;
  const canonicalReady = canonicalEnvironmentAvailable(canonical, Date.parse(now));
  const canonicalSubject = canonicalReady
    ? canonical?.response?.presentation?.subject ?? null
    : null;
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | "PASS" | "FAIL" | "superseded">("all");
  const apiStatus = status === "PASS" || status === "FAIL" ? status : undefined;
  const receiptsApi = useApi(
    verificationCanonicalClients.receiptsList.operation,
    // The typed receipts route is a legacy compatibility projection. Never
    // send env=real for a canonical product selection; the scoped universal
    // overview below is the source for Local, Remote, and Offline contexts.
    ready && environment === "demo" ? { query: { env: environment, status: apiStatus } } : null,
  );
  const universalApi = useApi(
    universalVerification.overview,
    canonicalSubject ? { query: canonicalSubject } : null,
  );
  const completionProjection = useMemo(
    () => (universalApi.data ? projectUniversalCompletionOverview(universalApi.data) : null),
    [universalApi.data],
  );
  const readableReceipts = useMemo(() => {
    if (!completionProjection) return [];
    return combineCompletionHistory(
      completionProjection.current.receipts,
      completionProjection.incomplete.receipts,
      (receipt) => receipt.payload.id,
    );
  }, [completionProjection]);
  // READ TIME, once for the screen. A signed receipt is append-only, so a PENDING one
  // keeps saying PENDING past its deadline; `resolveStoredVerdict` (via
  // `resolveReceiptVerdicts`) is the one engine that says what it means today, and this
  // page holds no deadline arithmetic of its own.
  const resolvedVerdicts = useMemo(
    () => resolveReceiptVerdicts(readableReceipts, now),
    [readableReceipts, now],
  );
  const prospectiveReceipts = useMemo(
    () => currentProspectiveReceipts(completionProjection?.current.receipts ?? []),
    [completionProjection],
  );
  const prospectiveReceiptIds = useMemo(
    () => currentAuthorityIds(prospectiveReceipts, (receipt) => receipt.payload.id),
    [prospectiveReceipts],
  );
  const retrospectiveReceipts = useMemo(
    () =>
      completionProjection
        ? retrospectiveCompletionHistory(
            completionProjection.current.receipts,
            completionProjection.incomplete.receipts,
          )
        : [],
    [completionProjection],
  );
  const retrospectiveReceiptIds = useMemo(
    () => new Set(retrospectiveReceipts.map((receipt) => receipt.payload.id)),
    [retrospectiveReceipts],
  );
  const retrospectiveReceiptCount = retrospectiveReceipts.length;
  const workTitleById = useMemo(() => {
    const titles = new Map<string, string>();
    for (const work of [
      ...(completionProjection?.current.work ?? []),
      ...(completionProjection?.incomplete.work ?? []),
    ]) {
      if (!titles.has(work.id)) titles.set(work.id, work.title);
    }
    return titles;
  }, [completionProjection]);
  // The single classifier (`classifyWorkSubject`), applied once per Work id and
  // looked up per receipt — this is what lets the ledger below distinguish the
  // product verifying itself from the product verifying real work, without a
  // second classification rule.
  const workSubjectById = useMemo(() => {
    const subjects = new Map<string, ReturnType<typeof classifyWorkSubject>>();
    for (const work of [
      ...(completionProjection?.current.work ?? []),
      ...(completionProjection?.incomplete.work ?? []),
    ]) {
      if (!subjects.has(work.id)) subjects.set(work.id, classifyWorkSubject(work.source.uri));
    }
    return subjects;
  }, [completionProjection]);
  const [subjectFilter, setSubjectFilter] = useState<"all" | WorkChainSubjectClass>("all");
  // Deliberately over EVERY readable receipt (current + incomplete history,
  // prospective AND retrospective) — not just `prospectiveReceipts`. Both real
  // -subject receipts in production are recorded RETROSPECTIVE, so a split
  // scoped to "prospective" would show zero real-subject rows and silently
  // hide the one real FAIL this fix exists to surface. Counts back the filter
  // tabs directly, so a tab's own number is never out of sync with what
  // selecting it actually shows.
  const readableSubjectCounts = useMemo(() => {
    const counts: Record<WorkChainSubjectClass, number> = {
      "real-subject": 0,
      "self-test-fixture": 0,
      "synthetic-fixture": 0,
    };
    for (const receipt of readableReceipts) {
      const subject = workSubjectById.get(receipt.payload.binding.workItemId);
      if (subject) counts[subject.class] += 1;
    }
    return counts;
  }, [readableReceipts, workSubjectById]);
  const realSubjectReceipts = useMemo(
    () =>
      readableReceipts.filter(
        (receipt) => workSubjectById.get(receipt.payload.binding.workItemId)?.class === "real-subject",
      ),
    [readableReceipts, workSubjectById],
  );
  const realSubjectPassCount = realSubjectReceipts.filter(
    (receipt) => verdictFrom(resolvedVerdicts, receipt, now) === "PASS",
  ).length;
  const realSubjectFailCount = realSubjectReceipts.filter(
    (receipt) => verdictFrom(resolvedVerdicts, receipt, now) === "FAIL",
  ).length;
  const realSubjectRetrospectiveCount = realSubjectReceipts.filter(
    (receipt) => receipt.payload.assurance === "RETROSPECTIVE",
  ).length;
  const fixtureReadableCount =
    readableSubjectCounts["self-test-fixture"] + readableSubjectCounts["synthetic-fixture"];
  const readableReceiptsBySubject = useMemo(() => {
    if (subjectFilter === "all") return readableReceipts;
    return readableReceipts.filter(
      (receipt) => workSubjectById.get(receipt.payload.binding.workItemId)?.class === subjectFilter,
    );
  }, [readableReceipts, subjectFilter, workSubjectById]);
  const trustKeyId = universalApi.data?.trust.keyId ?? null;
  const lastFetchedAt = useLastFetchedAt(universalApi.data ?? receiptsApi.data);

  const catalog = useMemo(() => {
    if (!environment) return [];
    return receiptsApi.data?.items ?? [];
  }, [environment, receiptsApi.data]);
  const supersededIds = useMemo(
    () => new Set(catalog.map((row) => row.supersedes).filter(Boolean)),
    [catalog],
  );
  const rows = useMemo(() => {
    let list = catalog;
    if (status === "PASS" || status === "FAIL")
      list = list.filter((receipt) => receipt.status === status);
    if (status === "superseded") list = list.filter((receipt) => supersededIds.has(receipt.id));
    const needle = query.trim().toLowerCase();
    if (needle) {
      list = list.filter(
        (receipt) =>
          receipt.id.toLowerCase().includes(needle) ||
          receipt.runId.toLowerCase().includes(needle) ||
          receipt.payloadHash.toLowerCase().includes(needle) ||
          receipt.signatureKeyId.toLowerCase().includes(needle) ||
          receipt.policyVersionRefs.some((ref) => ref.toLowerCase().includes(needle)),
      );
    }
    return [...list].sort(
      (a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime(),
    );
  }, [catalog, query, status, supersededIds]);

  if (env && !ready) {
    return (
      <PageFrame page="receipts" wide eyebrow="Prove" title="Proof" lede={RECEIPTS_LEDE}>
        <PagePending title="Loading environment">
          Loading receipts for the active Environment.
        </PagePending>
      </PageFrame>
    );
  }

  if (canonical && !canonicalReady) {
    return (
      <PageFrame page="receipts" wide eyebrow="Prove" title="Proof" lede={RECEIPTS_LEDE}>
        <Callout tone="unknown" title="Environment context required">
          Receipts remain unavailable until the selected Environment is authorized and its current
          location is confirmed. This page will not substitute the retired local fixture scope.
        </Callout>
      </PageFrame>
    );
  }

  if (receiptsApi.error) {
    return (
      <PageFrame page="receipts" wide eyebrow="Prove" title="Proof" lede={RECEIPTS_LEDE}>
        <PageError
          title="Receipts could not load"
          error={receiptsApi.error}
          onRetry={() => void receiptsApi.mutate()}
        />
      </PageFrame>
    );
  }

  if (!environment && !canonicalReady) {
    return (
      <PageFrame page="receipts" wide eyebrow="Prove" title="Proof" lede={RECEIPTS_LEDE}>
        <PagePending title="Environment unavailable">
          Receipts are environment-scoped. Pick an environment to inspect the ledger.
        </PagePending>
      </PageFrame>
    );
  }

  const passCount =
    status === "PASS"
      ? catalog.length
      : catalog.filter((receipt) => receipt.status === "PASS").length;
  const failCount =
    status === "FAIL"
      ? catalog.length
      : catalog.filter((receipt) => receipt.status === "FAIL").length;
  const supersededCount = catalog.filter((receipt) => supersededIds.has(receipt.id)).length;
  return (
    <PageFrame page="receipts" wide eyebrow="Prove" title="Proof" lede={RECEIPTS_LEDE}>
      {environment === "demo" ? (
        <Callout tone="neutral" title="Sample fixture proof projection">
          Canonical signed receipts are isolated to the verified Local Environment. Sample fixture receipt metadata
          remains available below.
        </Callout>
      ) : universalApi.error ? (
        <Callout tone="unknown" title="Canonical proof unavailable">
          Signed envelopes and trust material could not be loaded. The operational projection below
          is not promoted to cryptographic proof.
        </Callout>
      ) : universalApi.isLoading && !universalApi.data ? (
        <PagePending title="Loading signed proof">
          Reading receipt envelopes, replay state, and trust material.
        </PagePending>
      ) : universalApi.data ? (
        <Section
          title="Signed verdicts"
          note={`Canonical ledger generated ${formatDateTime(universalApi.data.generatedAt)}. Trust key ${universalApi.data.trust.keyId} is ${universalApi.data.trust.status}. Only completion-aware receipts with current Work, contract, and attempt parents count as signed verdicts.`}
        >
            <ScopeTabs
              value={subjectFilter}
              onChange={setSubjectFilter}
              options={[
                {
                  key: "all",
                  label: `All (${readableReceipts.length})`,
                  actionId: RECEIPTS_SUBJECT_FILTER_ALL,
                },
                {
                  key: "real-subject",
                  label: `Real subject (${readableSubjectCounts["real-subject"]})`,
                  actionId: RECEIPTS_SUBJECT_FILTER_REAL,
                },
                {
                  key: "self-test-fixture",
                  label: `Self-test (${readableSubjectCounts["self-test-fixture"]})`,
                  actionId: RECEIPTS_SUBJECT_FILTER_SELF_TEST,
                },
                {
                  key: "synthetic-fixture",
                  label: `Synthetic (${readableSubjectCounts["synthetic-fixture"]})`,
                  actionId: RECEIPTS_SUBJECT_FILTER_SYNTHETIC,
                },
              ]}
            />
          <details className="rounded-lg border p-3" data-proof-ledger-summary>
          <summary className="cursor-pointer type-body font-medium">Ledger totals and subject classifications</summary>
          <StatGrid cols={4}>
            <Stat label="Prospective signed receipts" value={prospectiveReceipts.length} />
            <Stat
              label="Prospective pass"
              value={
                prospectiveReceipts.filter(
                  (receipt) => verdictFrom(resolvedVerdicts, receipt, now) === "PASS",
                ).length
              }
              tone="pass"
            />
            <Stat
              label="Recorded retrospective"
              value={retrospectiveReceiptCount}
              tone={retrospectiveReceiptCount > 0 ? "unknown" : undefined}
            />
            <Stat
              label="Incomplete history"
              value={completionProjection?.incomplete.receipts.length ?? 0}
              tone="unknown"
            />
          </StatGrid>
          {/* The stat grid above blends the product verifying itself into one
              "Prospective pass" number. `classifyWorkSubject` (the same
              classifier the per-item chain view uses) splits EVERY readable
              receipt below — deliberately not scoped to "prospective": both
              real-subject receipts on record are RETROSPECTIVE, so a
              prospective-only split would report zero real-subject receipts
              and hide the one real FAIL entirely. */}
          <StatGrid cols={4}>
            <Stat
              label="Self-verification (fixture)"
              value={fixtureReadableCount}
              tone="unknown"
              foot="the product checking itself"
            />
            <Stat
              label="Real-subject receipts"
              value={realSubjectReceipts.length}
              tone="accent"
              foot="work outside the system's own fixtures"
            />
            <Stat
              label="Real-subject pass"
              value={realSubjectPassCount}
              tone={realSubjectPassCount > 0 ? "pass" : undefined}
            />
            <Stat
              label="Real-subject fail"
              value={realSubjectFailCount}
              tone={realSubjectFailCount > 0 ? "fail" : undefined}
            />
          </StatGrid>
          <Callout
            tone={realSubjectFailCount > 0 ? "fail" : "neutral"}
            title="Fixture receipts are not proof of real work"
          >
            {fixtureReadableCount} of {readableReceipts.length} readable receipts verify the
            product's own fixtures (self-test canaries and synthetic ui-gate subjects). Only{" "}
            {realSubjectReceipts.length} verify a real subject, and{" "}
            {realSubjectFailCount > 0
              ? `${realSubjectFailCount} of ${realSubjectReceipts.length} failed`
              : realSubjectReceipts.length > 0
                ? "all of them passed"
                : "none have been recorded yet"}
            {realSubjectRetrospectiveCount > 0
              ? ` (${realSubjectRetrospectiveCount} recorded retrospectively — a recorded fact, not prospective assurance)`
              : ""}
            .
          </Callout>
          {(completionProjection?.incomplete.receipts.length ?? 0) > 0 ? (
            <Callout tone="unknown" title="Historical receipts are incomplete">
              Their recorded verdict, assurance, replay, and signature fields remain inspectable
              facts, but missing canonical completion metadata or current durable parents means they
              cannot authorize enforcement.
            </Callout>
          ) : null}
          {retrospectiveReceiptCount > 0 ? (
            <Callout tone="unknown" title="Retrospective receipts are recorded facts">
              {retrospectiveReceiptCount} structurally current receipt
              {retrospectiveReceiptCount === 1 ? " is" : "s are"} retrospective. Its recorded
              verdict, replay, and signature remain inspectable, but it cannot count as prospective
              assurance or authorize enforcement.
            </Callout>
          ) : null}
          </details>
          <DataTable
            rows={readableReceiptsBySubject}
            rowKey={(receipt) => receipt.payload.id}
            emptyState={{
              title: "No signed verdicts",
              description:
                "Proof appears after an independent verifier evaluates admitted evidence against an accepted contract and issues a receipt.",
            }}
            columns={[
              {
                key: "receipt",
                header: "Verified work",
                cellClassName: "whitespace-normal",
                render: (receipt) => (
                  <div
                    className="flex min-w-0 flex-col gap-1"
                    data-receipt-id={receipt.payload.id}
                  >
                    {/* Plain-language first: a reader who never learns what a receipt id
                        is still gets what was verified and how it came out. The ids stay
                        in the row, one disclosure away. */}
                    <a className="font-medium text-foreground underline underline-offset-4" data-receipt-plain href={href("receipts", encodeURIComponent(receipt.payload.id))}>
                      {workTitleById.get(receipt.payload.binding.workItemId) ?? UNNAMED_WORK}
                    </a>
                    <span className="type-subtext text-muted-foreground">
                      {receiptOutcomeSentence(verdictFrom(resolvedVerdicts, receipt, now))}
                    </span>
                    <details>
                      <summary className="cursor-pointer type-subtext text-muted-foreground">
                        Reference IDs
                      </summary>
                      <div
                        className="mt-1 flex min-w-0 flex-col gap-0.5 break-all"
                        data-receipt-ids
                      >
                        <a className="underline" href={href("receipts", encodeURIComponent(receipt.payload.id))}><Mono>Receipt {receipt.payload.id}</Mono></a>
                        <Mono>Run {receipt.payload.runId}</Mono>
                        <Mono>Work {receipt.payload.binding.workItemId}</Mono>
                      </div>
                    </details>
                  </div>
                ),
              },
              {
                key: "subject",
                header: "Subject",
                render: (receipt) => {
                  const subject = workSubjectById.get(receipt.payload.binding.workItemId);
                  if (!subject) return <ToneBadge tone="unknown">Unclassified</ToneBadge>;
                  return (
                    <div className="flex flex-col gap-1" data-subject-class={subject.class}>
                      <ToneBadge tone={subjectTone(subject.class)}>
                        {SUBJECT_CLASS_SHORT_LABEL[subject.class]}
                      </ToneBadge>
                    </div>
                  );
                },
              },
              {
                key: "verdict",
                header: "Verdict",
                render: (receipt) =>
                  prospectiveReceiptIds.has(receipt.payload.id) ? (
                    <Verdict status={verdictFrom(resolvedVerdicts, receipt, now)} />
                  ) : retrospectiveReceiptIds.has(receipt.payload.id) ? (
                    <div className="flex flex-col gap-1" data-completion-state="retrospective">
                      <ToneBadge tone="unknown">RETROSPECTIVE</ToneBadge>
                      <span className="type-subtext text-muted-foreground">
                        Recorded {verdictFrom(resolvedVerdicts, receipt, now)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1" data-completion-state="incomplete">
                      <ToneBadge tone="unknown">INCOMPLETE</ToneBadge>
                      <span className="type-subtext text-muted-foreground">
                        Recorded {verdictFrom(resolvedVerdicts, receipt, now)}
                      </span>
                    </div>
                  ),
              },
              {
                key: "checks",
                header: "Checks / evidence",
                render: (receipt) => {
                  const evidenceCount = new Set(
                    receipt.payload.determinations.flatMap(
                      (determination) => determination.evidenceArtifactIds,
                    ),
                  ).size;
                  return `${receipt.payload.determinations.length} / ${evidenceCount}`;
                },
              },
              {
                key: "verifier",
                header: "Verifier",
                render: (receipt) => <Mono>{receipt.payload.verifier.actorId}</Mono>,
              },
              {
                key: "assurance",
                header: "Assurance",
                render: (receipt) => (
                  <div className="flex flex-col gap-1">
                    <ToneBadge
                      tone={prospectiveReceiptIds.has(receipt.payload.id) ? "pass" : "unknown"}
                    >
                      {prospectiveReceiptIds.has(receipt.payload.id)
                        ? receipt.payload.assurance
                        : retrospectiveReceiptIds.has(receipt.payload.id)
                          ? "RETROSPECTIVE"
                          : "INCOMPLETE"}
                    </ToneBadge>
                    {prospectiveReceiptIds.has(receipt.payload.id) ? null : (
                      <span className="type-subtext text-muted-foreground">
                        {retrospectiveReceiptIds.has(receipt.payload.id)
                          ? "Recorded without completion authority"
                          : `Recorded ${receipt.payload.assurance}`}
                      </span>
                    )}
                  </div>
                ),
              },
              {
                key: "replay",
                header: "Replay",
                render: (receipt) => (
                  <ToneBadge
                    tone={
                      prospectiveReceiptIds.has(receipt.payload.id) &&
                      receipt.payload.replay.status === "REPLAYED"
                        ? "pass"
                        : prospectiveReceiptIds.has(receipt.payload.id) &&
                            receipt.payload.replay.status === "BLOCKED"
                          ? "fail"
                          : "neutral"
                    }
                  >
                    {receipt.payload.replay.status}
                  </ToneBadge>
                ),
              },
              {
                key: "signed",
                header: "Signed",
                render: (receipt) => (
                  /* The trust key is a constant across these rows and is already named
                     once in the section note above, so a row only reports whether it was
                     signed under that same key. The key id stays on the row. */
                  <div
                    className="flex min-w-0 flex-col gap-0.5"
                    data-signature-key-id={receipt.signature.keyId}
                  >
                    <span>
                      {receipt.signature.keyId === trustKeyId ? "Active trust key" : "Other key"}
                    </span>
                    {receipt.signature.keyId === trustKeyId ? null : (
                      <Mono>{receipt.signature.keyId}</Mono>
                    )}
                    <span className="type-subtext text-muted-foreground">
                      {formatRelativeTime(receipt.payload.issuedAt)}
                    </span>
                  </div>
                ),
              },
            ]}
          />
        </Section>
      ) : null}
      <details className="rounded-lg border p-4" data-proof-registrar>
        <summary className="cursor-pointer type-body font-medium">About registrar countersignatures</summary>
        <RegistrarAssuranceSection />
      </details>
      {canonicalReady ? (
        <Callout tone="neutral" title="Compatibility projection not queried">
          The selected Environment is read through the canonical signed ledger above. The retired
          compatibility receipt projection is not used for this target.
        </Callout>
      ) : null}
      {!canonicalReady ? <details className="rounded-lg border p-4" data-proof-compatibility>
      <summary className="cursor-pointer type-body font-medium">Operational receipt projection ({catalog.length})</summary>
      <Section
        title="Operational receipt projection"
        note="These compatibility records support existing control workflows but omit signature bytes and independently obtained trust material."
        actions={
          <ScopeTabs
            value={status}
            onChange={setStatus}
            options={[
              { key: "all", label: `All (${catalog.length})`, actionId: RECEIPTS_FILTER_ALL },
              { key: "PASS", label: `Pass (${passCount})`, actionId: RECEIPTS_FILTER_PASS },
              { key: "FAIL", label: `Fail (${failCount})`, actionId: RECEIPTS_FILTER_FAIL },
              {
                key: "superseded",
                label: `Superseded (${supersededCount})`,
                actionId: RECEIPTS_FILTER_SUPERSEDED,
              },
            ]}
          />
        }
      >
        <LastFetched at={lastFetchedAt} stale={Boolean(receiptsApi.error && receiptsApi.data)} />
        <StatGrid cols={3}>
          <Stat label="Receipt records" value={formatNumber(catalog.length)} />
          <Stat
            label="Evidence bindings"
            value={formatNumber(
              catalog.reduce((total, receipt) => total + receipt.evidenceHashes.length, 0),
            )}
          />
          <Stat label="Superseded" value={formatNumber(supersededCount)} />
        </StatGrid>
        {receiptsApi.isLoading ? (
          <PagePending title="Loading receipts">
            Reading the typed receipts projection for the selected environment.
          </PagePending>
        ) : null}
        <Callout tone="unknown" title="Cryptographic verification unavailable">
          These compatibility records retain evidence and policy bindings, but do not include a
          verifiable signed envelope.
        </Callout>
        <DataTable
          toolbar={
            <FilterToolbar>
              <FilterField label="Search" wide>
                <ListSearch
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by receipt, run, hash, key or policy..."
                  aria-label="Search receipts by receipt, run, hash, key or policy"
                />
              </FilterField>
            </FilterToolbar>
          }
          emptyState={{
            title: catalog.length === 0 ? "No receipts yet" : "No receipts match",
            description:
              catalog.length === 0
                ? "Active Environments stay empty until a completed run issues a receipt record."
                : "Clear filters to inspect every receipt record.",
          }}
          rows={rows}
          rowKey={(receipt) => receipt.id}
          onRowClick={(receipt) => onNavigate(href("receipts", receipt.id))}
          columns={receiptsColumns(catalog, onNavigate)}
        />
      </Section>
      </details> : null}
    </PageFrame>
  );
}

export function ReceiptDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  const env = useVerificationEnvOptional();
  const receiptId = decodedReceiptId(id);
  if (receiptId.startsWith("receipt:") && env?.environment !== "demo") {
    return <SignedReceiptDetailPage id={receiptId} onNavigate={onNavigate} />;
  }
  return <CompatibilityReceiptDetailPage id={receiptId} onNavigate={onNavigate} />;
}

function CompatibilityReceiptDetailPage({ id, onNavigate }: VerificationDetailPageProps) {
  const env = useVerificationEnvOptional();
  const canonical = useCanonicalEnvironmentOptional();
  const environment = env?.environment ?? null;
  const canonicalReady = canonicalEnvironmentAvailable(canonical);
  const compatibilityAllowed = !canonical || environment === "demo";
  const detailApi = useApi(
    verificationCanonicalClients.receiptsGet.operation,
    compatibilityAllowed && environment === "demo"
      ? { params: { id }, query: { env: environment } }
      : null,
  );
  const listApi = useApi(
    verificationCanonicalClients.receiptsList.operation,
    compatibilityAllowed && environment === "demo" ? { query: { env: environment } } : null,
  );
  const artifactsApi = useApi(
    verificationCanonicalClients.artifactsList.operation,
    compatibilityAllowed && environment === "demo" ? { query: { env: environment } } : null,
  );
  const catalog = listApi.data?.items ?? [];
  const receipt = detailApi.data?.receipt ?? catalog.find((row) => row.id === id);
  const correction = receipt ? childReceipt(catalog, receipt.id) : null;
  const parent = receipt?.supersedes
    ? (catalog.find((row) => row.id === receipt.supersedes) ?? null)
    : null;
  const artifacts = receipt ? artifactRows(receipt, artifactsApi.data?.items ?? []) : [];
  const assurance = detailApi.data?.assurance ?? null;

  const lede =
    "One receipt record: verdict, recorded evidence hashes, policy versions, supersession, and reported signing-key metadata.";

  if (canonical && !canonicalReady) {
    return (
      <PageFrame page="receipt" wide eyebrow="Prove" title="Receipt" lede={lede}>
        <Callout tone="unknown" title="Environment context required">
          This receipt cannot be read until the selected Environment is authorized and its current
          location is confirmed.
        </Callout>
      </PageFrame>
    );
  }
  if (canonical && canonicalReady) {
    return (
      <PageFrame page="receipt" wide eyebrow="Prove" title="Receipt" lede={lede}>
        <Callout tone="unknown" title="Canonical receipt required">
          This compatibility receipt is not part of the selected Environment&apos;s canonical
          signed ledger.
        </Callout>
        <PageLink onNavigate={onNavigate} to="receipts">
          Back to receipts
        </PageLink>
      </PageFrame>
    );
  }

  if (detailApi.isLoading) {
    return (
      <PageFrame page="receipt" wide eyebrow="Prove" title="Receipt" lede={lede}>
        <PagePending title={`Loading ${id}`}>
          Reading receipt detail from verificationCanonicalClients.receiptsGet.operation.
        </PagePending>
      </PageFrame>
    );
  }

  if (detailApi.error) {
    return (
      <PageFrame page="receipt" wide eyebrow="Prove" title="Receipt" lede={lede}>
        <PageError
          title={`No receipt loaded for ${id}`}
          error={detailApi.error}
          onRetry={() => void detailApi.mutate()}
        />
      </PageFrame>
    );
  }

  if (!receipt) {
    return (
      <PageFrame page="receipt" wide eyebrow="Prove" title="Receipt" lede={lede}>
        <PagePending title={`No receipt loaded for ${id}`}>
          Open a receipt from the selected environment ledger.{" "}
          <PageLink onNavigate={onNavigate} to="receipts">
            Back to receipts
          </PageLink>
          .
        </PagePending>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      page="receipt"
      wide
      eyebrow="Prove"
      title={receipt.id}
      lede={`Issued ${formatDateTime(receipt.issuedAt)} for run ${receipt.runId} by ${receipt.engineVersion}.`}
      actions={
        <ButtonRow>
          <ActionButton
            actionId={RECEIPTS_EXPORT_ACTION}
            data-action-id={RECEIPTS_EXPORT_ACTION}
            data-receipt-id={receipt.id}
            onClick={() => exportReceiptRecord(receipt)}
          >
            Export record
          </ActionButton>
        </ButtonRow>
      }
    >
      <ButtonRow>
        <Verdict status={receipt.status} />
        <ToneBadge tone={assurance?.currentReceiptEligible ? "pass" : "unknown"} size="lg">
          {assurance?.displayLabel ?? "receipt metadata"}
        </ToneBadge>
        <ToneBadge tone="neutral">{`${formatNumber(receipt.checkCount)} checks`}</ToneBadge>
      </ButtonRow>
      <Callout
        tone={assurance?.currentReceiptEligible ? "neutral" : "unknown"}
        title={
          assurance?.currentReceiptEligible
            ? "Verified completion assurance"
            : "Receipt is not current verified assurance"
        }
      >
        {assurance?.currentReceiptEligible
          ? "This receipt detail includes an eligible signed assurance projection."
          : `Named blockers: ${
              assurance?.blockedReasons.length
                ? assurance.blockedReasons.join(", ")
                : "assurance projection unavailable"
            }.`}
      </Callout>
      {artifactsApi.error ? (
        <Callout tone="unknown" title="Evidence metadata unavailable">
          The receipt hashes remain visible, but the live evidence catalog could not be loaded for
          this environment.
        </Callout>
      ) : null}

      <Card>
        <CardContent>
          <Fields>
            <Field label="Payload hash">
              <Mono>{receipt.payloadHash}</Mono>
            </Field>
            <Field label="Reported key metadata">
              <Mono>
                {receipt.signatureKeyId} · {receipt.signatureAlgorithm}
              </Mono>
            </Field>
            <Field label="Assurance class">{assurance?.assuranceClass ?? "none"}</Field>
            <Field label="Assurance authority">{assurance?.authority ?? "unknown"}</Field>
            <Field label="Assurance scope">{assurance?.scope ?? "unknown"}</Field>
            <Field label="Assurance mode">{assurance?.mode ?? "unknown"}</Field>
            <Field label="Current receipt eligible">
              {assurance?.currentReceiptEligible ? "true" : "false"}
            </Field>
            <Field label="Assurance blockers">
              {assurance?.blockedReasons.length ? (
                <div className="flex flex-wrap gap-1">
                  {assurance.blockedReasons.map((reason) => (
                    <ToneBadge key={reason} tone="unknown">
                      {reason}
                    </ToneBadge>
                  ))}
                </div>
              ) : (
                "None"
              )}
            </Field>
          </Fields>
        </CardContent>
      </Card>

      {parent ? (
        <Callout tone="neutral" title={`Correction for ${parent.id}.`}>
          {receipt.correctionReason ?? "A later receipt corrected the original conclusion."}
        </Callout>
      ) : null}
      {correction ? (
        <Callout tone="neutral" title={`Superseded by ${correction.id}.`}>
          <ButtonRow>
            <span>
              This receipt remains audit history, but the correction is the current receipt for the
              run.
            </span>
            <ActionButton
              actionId={RECEIPTS_OPEN_DETAIL_ACTION}
              onClick={() => onNavigate(href("receipts", correction.id))}
            >
              Open correction
            </ActionButton>
          </ButtonRow>
        </Callout>
      ) : null}

      <Card>
        <CardContent>
          <Fields>
            <Field label="Evidence hashes">
              <div className="flex flex-col gap-1">
                {receipt.evidenceHashes.map((hash) => (
                  <Mono key={hash}>{hash}</Mono>
                ))}
              </div>
            </Field>
            <Field label="Policy versions">
              <div className="flex flex-wrap gap-1">
                {receipt.policyVersionRefs.map((ref) => (
                  <ToneBadge key={ref} tone="accent">
                    {ref}
                  </ToneBadge>
                ))}
              </div>
            </Field>
          </Fields>
        </CardContent>
      </Card>

      <DataTable
        emptyState={{
          title: "No evidence artifacts resolved",
          description:
            "The receipt still carries hashes; artifact metadata is unavailable in this environment.",
        }}
        rows={artifacts}
        rowKey={(artifact) => artifact.id}
        columns={[
          { key: "artifact", header: "Artifact", render: (artifact) => artifact.label },
          { key: "source", header: "Source", render: (artifact) => artifact.sourceKind },
          {
            key: "hash",
            header: "Content hash",
            render: (artifact) => <Mono>{artifact.contentHash}</Mono>,
          },
          {
            key: "state",
            header: "State",
            render: (artifact) => (
              <ToneBadge tone={artifact.state === "active" ? "pass" : "neutral"}>
                {artifact.state}
              </ToneBadge>
            ),
          },
        ]}
      />

      <ButtonRow>
        <ActionButton actionId="nav.runs" onClick={() => onNavigate(href("runs", receipt.runId))}>
          Open run
        </ActionButton>
        <ActionButton actionId="nav.receipts" onClick={() => onNavigate(href("receipts"))}>
          Back to ledger
        </ActionButton>
      </ButtonRow>
    </PageFrame>
  );
}
